import React, { useRef, useEffect, useMemo } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { atom } from "jotai";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { ConvexGeometry } from "three/examples/jsm/geometries/ConvexGeometry.js";
import { nnSegmentationResultAtom, contourVersionAtom } from "../../atoms/segmentationAtoms";
import { getCornerstoneTools } from "../../utils/dicomLoader";
import { type WorldPolyline } from "../../utils/contourToWorldPoints";
import { PLANE_COLORS, PLANE_COLORS_HEX } from "../../constants/planeColors";
import { resolveAnnotationPlane } from "../../utils/planeResolver";
import { isLoadedFromMask } from "../../utils/annotationFlags";

const HULL_COLOR = 0xa78bfa; // purple-400
const HULL_OPACITY = 0.15;

/** Convex hull volume in mm³, computed from the 3D mesh geometry. */
export const hullVolumeMm3Atom = atom<number | null>(null);

/** Principal axis measurements from the hull's oriented bounding box. */
export interface HullAxis {
  length: number; // mm
  color: string; // hex CSS color
}
export const hullAxesAtom = atom<HullAxis[] | null>(null);

/**
 * Triangulate a closed planar polygon expressed as 3D world-space points.
 * Builds an orthonormal basis on the polygon's plane, projects to 2D, runs
 * THREE.ShapeUtils.triangulateShape for an earcut-style triangulation, then
 * returns a BufferGeometry whose vertices are the original 3D points.
 * Returns null on degenerate input (collinear points, fewer than 3 vertices).
 */
function buildPolygonFillGeometry(points: number[][]): THREE.BufferGeometry | null {
  if (points.length < 3) return null;
  const verts = points.map(([x, y, z]) => new THREE.Vector3(x, y, z));

  // Plane normal — find two non-collinear edges.
  const origin = verts[0];
  const u = new THREE.Vector3().subVectors(verts[1], origin);
  let normal: THREE.Vector3 | null = null;
  for (let i = 2; i < verts.length; i++) {
    const w = new THREE.Vector3().subVectors(verts[i], origin);
    const n = new THREE.Vector3().crossVectors(u, w);
    if (n.lengthSq() > 1e-10) {
      normal = n.normalize();
      break;
    }
  }
  if (!normal) return null;

  const uHat = u.clone().normalize();
  const vHat = new THREE.Vector3().crossVectors(normal, uHat).normalize();

  const projected = verts.map((p) => {
    const d = new THREE.Vector3().subVectors(p, origin);
    return new THREE.Vector2(d.dot(uHat), d.dot(vHat));
  });

  const tris = THREE.ShapeUtils.triangulateShape(projected, []);
  if (tris.length === 0) return null;

  const positions = new Float32Array(verts.length * 3);
  for (let i = 0; i < verts.length; i++) {
    positions[i * 3] = verts[i].x;
    positions[i * 3 + 1] = verts[i].y;
    positions[i * 3 + 2] = verts[i].z;
  }
  const indices: number[] = [];
  for (const [a, b, c] of tris) indices.push(a, b, c);

  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geom.setIndex(indices);
  geom.computeVertexNormals();
  return geom;
}

/** Resample a closed ring to exactly N evenly-spaced points by perimeter. */
function resampleRing(points: number[][], N: number): [number, number, number][] {
  if (points.length === 0) return [];
  const segLens: number[] = [];
  let total = 0;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1, z1] = points[i];
    const [x2, y2, z2] = points[(i + 1) % points.length];
    const d = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2 + (z2 - z1) ** 2);
    segLens.push(d);
    total += d;
  }
  if (total === 0) {
    const [x, y, z] = points[0] as [number, number, number];
    return Array(N)
      .fill(0)
      .map(() => [x, y, z]);
  }
  const step = total / N;
  const out: [number, number, number][] = [];
  let segIdx = 0;
  let acc = 0;
  for (let k = 0; k < N; k++) {
    const target = k * step;
    while (segIdx < segLens.length - 1 && acc + segLens[segIdx] < target) {
      acc += segLens[segIdx];
      segIdx++;
    }
    const segLen = segLens[segIdx] || 1;
    const t = (target - acc) / segLen;
    const [x1, y1, z1] = points[segIdx];
    const [x2, y2, z2] = points[(segIdx + 1) % points.length];
    out.push([x1 + t * (x2 - x1), y1 + t * (y2 - y1), z1 + t * (z2 - z1)]);
  }
  return out;
}

/**
 * Build a "vacuum-seal" lofted mesh from axial slice contours. Naively
 * connects matching ring points between consecutive slices and caps the
 * top/bottom slices via a centroid fan. Preserves concave geometry that a
 * convex hull would smooth away. Only consumes axial polylines — companion
 * planes are ignored.
 */
function buildLoftedMesh(polylines: WorldPolyline[]): THREE.BufferGeometry | null {
  const bySlice = new Map<number, WorldPolyline[]>();
  for (const p of polylines) {
    if (p.plane !== "axial" || p.points.length < 3) continue;
    const arr = bySlice.get(p.sliceIndex) ?? [];
    arr.push(p);
    bySlice.set(p.sliceIndex, arr);
  }
  const sliceIdxs = Array.from(bySlice.keys()).sort((a, b) => a - b);
  if (sliceIdxs.length < 2) return null;

  const N = 64;
  const rings: [number, number, number][][] = [];
  for (const idx of sliceIdxs) {
    const polys = bySlice.get(idx)!;
    // Multi-contour slices: pick the largest (most points) — naive but works
    // for the typical lesion case.
    const main = polys.reduce(
      (best, p) => (p.points.length > (best?.points.length ?? 0) ? p : best),
      polys[0],
    );
    rings.push(resampleRing(main.points, N));
  }

  const positions: number[] = [];
  for (const ring of rings) {
    for (const [x, y, z] of ring) positions.push(x, y, z);
  }
  const indices: number[] = [];

  // Side walls
  for (let r = 0; r < rings.length - 1; r++) {
    const off0 = r * N;
    const off1 = (r + 1) * N;
    for (let i = 0; i < N; i++) {
      const j = (i + 1) % N;
      indices.push(off0 + i, off0 + j, off1 + j);
      indices.push(off0 + i, off1 + j, off1 + i);
    }
  }

  // Helper for cap fans
  const addCap = (ring: [number, number, number][], ringOff: number, flip: boolean) => {
    let cx = 0,
      cy = 0,
      cz = 0;
    for (const [x, y, z] of ring) {
      cx += x;
      cy += y;
      cz += z;
    }
    cx /= ring.length;
    cy /= ring.length;
    cz /= ring.length;
    const centroidIdx = positions.length / 3;
    positions.push(cx, cy, cz);
    for (let i = 0; i < N; i++) {
      const j = (i + 1) % N;
      if (flip) indices.push(centroidIdx, ringOff + j, ringOff + i);
      else indices.push(centroidIdx, ringOff + i, ringOff + j);
    }
  };
  addCap(rings[0], 0, true); // top
  addCap(rings[rings.length - 1], (rings.length - 1) * N, false); // bottom

  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", new THREE.BufferAttribute(new Float32Array(positions), 3));
  geom.setIndex(indices);
  geom.computeVertexNormals();
  return geom;
}

/**
 * Compute signed volume of a triangle mesh using the divergence theorem.
 */
function computeMeshVolume(geometry: THREE.BufferGeometry): number {
  const pos = geometry.getAttribute("position");
  const idx = geometry.getIndex();
  let vol = 0;
  if (idx) {
    for (let i = 0; i < idx.count; i += 3) {
      const a = new THREE.Vector3().fromBufferAttribute(pos, idx.getX(i));
      const b = new THREE.Vector3().fromBufferAttribute(pos, idx.getX(i + 1));
      const c = new THREE.Vector3().fromBufferAttribute(pos, idx.getX(i + 2));
      vol += a.dot(new THREE.Vector3().crossVectors(b, c)) / 6;
    }
  } else {
    for (let i = 0; i < pos.count; i += 3) {
      const a = new THREE.Vector3().fromBufferAttribute(pos, i);
      const b = new THREE.Vector3().fromBufferAttribute(pos, i + 1);
      const c = new THREE.Vector3().fromBufferAttribute(pos, i + 2);
      vol += a.dot(new THREE.Vector3().crossVectors(b, c)) / 6;
    }
  }
  return Math.abs(vol);
}

/**
 * Build measurement axes aligned to the DICOM frame (rowCos, colCos,
 * sliceDir) rather than PCA. For each direction, project all points onto it,
 * find min/max, and return the corresponding extent + endpoint pair.
 * Sorted longest-first to match the rank-color convention.
 */
function buildAnatomicalAxes(
  points: THREE.Vector3[],
  axes: {
    rowCos: [number, number, number];
    colCos: [number, number, number];
    sliceDir: [number, number, number];
  },
): { dir: THREE.Vector3; extent: number; minPt: THREE.Vector3; maxPt: THREE.Vector3 }[] {
  const dirs = [axes.rowCos, axes.colCos, axes.sliceDir].map((d) =>
    new THREE.Vector3(d[0], d[1], d[2]).normalize(),
  );
  const centroid = new THREE.Vector3();
  for (const p of points) centroid.add(p);
  centroid.divideScalar(points.length);

  const out: { dir: THREE.Vector3; extent: number; minPt: THREE.Vector3; maxPt: THREE.Vector3 }[] =
    [];
  for (const dir of dirs) {
    let pMin = Infinity;
    let pMax = -Infinity;
    for (const p of points) {
      const d = new THREE.Vector3().subVectors(p, centroid).dot(dir);
      if (d < pMin) pMin = d;
      if (d > pMax) pMax = d;
    }
    // Endpoints lie on the line through centroid along dir, extending to the
    // bounding-box projection extents. They may sit outside the wrap surface
    // — by design, so each axis represents the organ's max extent in that
    // direction even where the mesh narrows.
    out.push({
      dir,
      extent: pMax - pMin,
      minPt: centroid.clone().addScaledVector(dir, pMin),
      maxPt: centroid.clone().addScaledVector(dir, pMax),
    });
  }
  out.sort((a, b) => b.extent - a.extent);
  return out;
}

interface ThreeState {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
}

export interface SegmentationViewer3DProps {
  /** Optional polylines to render. When provided, the labeling
   * nnSegmentationResultAtom is ignored — used by the measure flow which has
   * its own atom and only ships axial contours. */
  polylinesOverride?: WorldPolyline[];
  /** When true, render each contour as a filled translucent disk in addition
   * to its outline. Useful when contours are stacked parallel slices and the
   * outline-only render reads as a thin sheet. Defaults to false. */
  fillContours?: boolean;
  /** Outer surface style.
   *  - "convex" (default): a convex hull around all polyline points — smooth
   *    but loses concavities. Used by labeling.
   *  - "loft": a lofted "vacuum seal" connecting consecutive axial slice
   *    contours naively (point i↔point i, capped at top/bottom). Preserves
   *    concave/U-shaped geometry that a convex hull would smooth away. */
  wrapStyle?: "convex" | "loft";
  /** When provided, axis lines are drawn along these three orthogonal DICOM
   * directions (rowCos / colCos / sliceDir) instead of along PCA principal
   * directions. Each axis length is the projection extent of the polyline
   * points along its direction — guarantees one axis tracks the slice
   * direction so the metric reflects the organ's z-extent. */
  anatomicalAxes?: {
    rowCos: [number, number, number];
    colCos: [number, number, number];
    sliceDir: [number, number, number];
  };
}

export const SegmentationViewer3D: React.FC<SegmentationViewer3DProps> = ({
  polylinesOverride,
  fillContours = false,
  wrapStyle = "convex",
  anatomicalAxes,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const threeRef = useRef<ThreeState | null>(null);
  const segResult = useAtomValue(nnSegmentationResultAtom);
  const contourVersion = useAtomValue(contourVersionAtom);
  const setHullVolume = useSetAtom(hullVolumeMm3Atom);
  const setHullAxes = useSetAtom(hullAxesAtom);

  // Build world-space polylines from cornerstone keyframe annotation state.
  // Cornerstone polylines are already in world coordinates (set by the tool
  // when the contour was drawn or by the region-grow companion sessions),
  // so no DICOM-affine reconstruction is needed — that reconstruction drifts
  // for non-axial planes when the captured cosines/origin don't exactly
  // match the rendered viewport.
  //
  // Re-derives whenever contourVersionAtom bumps (drawn / saved / deleted /
  // region-grow committed). Also depends on segResult so that nn-interactive
  // commits trigger a rebuild even before contourVersion bumps.
  //
  // The measure flow passes polylinesOverride and bypasses this entirely.
  const polylines = useMemo<WorldPolyline[]>(() => {
    if (polylinesOverride) return polylinesOverride;

    const all: WorldPolyline[] = [];
    try {
      const csTools = getCornerstoneTools();
      const allAnns: any[] = csTools.annotation.state.getAllAnnotations() ?? [];
      for (const ann of allAnns) {
        if (ann?.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") continue;
        // Exclude saved-mask contours so previously-saved stones don't appear
        // alongside the in-progress one in the 3D viewer.
        if (isLoadedFromMask(ann)) continue;
        const polyline = ann?.data?.contour?.polyline;
        if (!polyline || polyline.length < 3) continue;
        // Plane resolution: _plane stamp → viewPlaneNormal classification.
        // If neither is usable we skip the contour and warn rather than
        // silently treating it as axial — a misclassified contour shows up
        // in the wrong plane's 3D loft and is hard to debug after the fact.
        const plane = resolveAnnotationPlane(ann);
        if (plane === null) {
          console.warn("[SegmentationViewer3D] Skipping contour with unresolvable plane", {
            uid: ann?.annotationUID,
            hasPlaneStamp: !!ann?._plane,
          });
          continue;
        }
        all.push({
          points: polyline.map((pt: number[]) => [pt[0], pt[1], pt[2]]),
          plane,
          sliceIndex: 0,
        });
      }
    } catch {
      /* cornerstone not initialized — fall through with empty polylines */
    }
    return all;
  }, [segResult, polylinesOverride, contourVersion]);

  // Init Three.js on mount
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x030712);
    container.appendChild(renderer.domElement);

    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 1000);
    const scene = new THREE.Scene();

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;

    const state: ThreeState = { renderer, scene, camera, controls };
    threeRef.current = state;

    renderer.setAnimationLoop(() => {
      controls.update();
      renderer.render(scene, camera);
    });

    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width === 0 || height === 0) return;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    });
    ro.observe(container);

    return () => {
      ro.disconnect();
      renderer.setAnimationLoop(null);
      renderer.dispose();
      container.removeChild(renderer.domElement);
      threeRef.current = null;
    };
  }, []);

  // Update geometry when polylines change
  useEffect(() => {
    const state = threeRef.current;
    if (!state) return;
    const { scene, camera, controls } = state;

    // Clear previous geometry
    while (scene.children.length > 0) {
      const child = scene.children[0];
      if (
        child instanceof THREE.Mesh ||
        child instanceof THREE.LineLoop ||
        child instanceof THREE.Line
      ) {
        (child.geometry as THREE.BufferGeometry).dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose());
        } else {
          (child.material as THREE.Material).dispose();
        }
      }
      scene.remove(child);
    }

    if (polylines.length === 0) {
      setHullVolume(null);
      setHullAxes(null);
      return;
    }

    // Collect all world-space points for the convex hull
    const allPoints: THREE.Vector3[] = [];
    const bbox = new THREE.Box3();

    // Build line loops
    for (const polyline of polylines) {
      const positions = new Float32Array(polyline.points.length * 3);
      for (let i = 0; i < polyline.points.length; i++) {
        const [x, y, z] = polyline.points[i];
        positions[i * 3] = x;
        positions[i * 3 + 1] = y;
        positions[i * 3 + 2] = z;
        const v = new THREE.Vector3(x, y, z);
        bbox.expandByPoint(v);
        allPoints.push(v);
      }
      const geom = new THREE.BufferGeometry();
      geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const color = PLANE_COLORS[polyline.plane] ?? 0xffffff;
      const mat = new THREE.LineBasicMaterial({ color });
      const loop = new THREE.LineLoop(geom, mat);
      scene.add(loop);

      // Filled disk for each closed contour. Triangulates the polygon in its
      // own plane (axial/coronal/sagittal contours are coplanar) and renders
      // it as a translucent purple mesh. Skipped when polyline is degenerate.
      if (fillContours && polyline.points.length >= 3) {
        const fillGeom = buildPolygonFillGeometry(polyline.points);
        if (fillGeom) {
          const fillMat = new THREE.MeshBasicMaterial({
            color: 0xa855f7,
            transparent: true,
            opacity: 0.35,
            side: THREE.DoubleSide,
            depthWrite: false,
          });
          scene.add(new THREE.Mesh(fillGeom, fillMat));
        }
      }
    }

    // Build outer surface mesh + principal axis measurements. The default
    // ConvexGeometry produces a smooth hull; "loft" connects axial slice
    // contours into a vacuum-seal surface that preserves concavities.
    if (allPoints.length >= 4) {
      const loftGeom = wrapStyle === "loft" ? buildLoftedMesh(polylines) : null;
      const wrapGeom: THREE.BufferGeometry =
        loftGeom ?? (new ConvexGeometry(allPoints) as THREE.BufferGeometry);
      const usingLoft = !!loftGeom;
      const wrapMat = new THREE.MeshPhysicalMaterial({
        color: HULL_COLOR,
        transparent: true,
        // Loft surface kept slightly more transparent than the convex hull
        // so the inner filled disks remain readable through it, but visible
        // enough that the outer wrap reads as a surface.
        opacity: usingLoft ? 0.2 : HULL_OPACITY,
        side: THREE.DoubleSide,
        depthWrite: false,
        roughness: 0.5,
        metalness: 0.05,
      });
      scene.add(new THREE.Mesh(wrapGeom, wrapMat));

      setHullVolume(computeMeshVolume(wrapGeom));

      // Lighting
      scene.add(new THREE.AmbientLight(0xffffff, 0.6));
      const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
      dirLight.position.set(1, 1, 1);
      scene.add(dirLight);

      // Anatomical axis lines: one per plane (axial / coronal / sagittal),
      // running along that plane's slice-direction vector, raycast to the
      // hull surface from the centroid. Colored to match the plane's contour
      // rings — blue for axial, green for coronal, red for sagittal — so the
      // green line is unambiguously "the coronal-plane axis", and likewise
      // for the others. Length = hull extent along that anatomical direction.
      const hullMesh = scene.children.find(
        (c) => c instanceof THREE.Mesh && (c.material as any) === wrapMat,
      ) as THREE.Mesh;
      const raycaster = new THREE.Raycaster();

      const centroid = new THREE.Vector3();
      for (const p of allPoints) centroid.add(p);
      centroid.divideScalar(allPoints.length);

      const axisData: HullAxis[] = [];

      if (anatomicalAxes) {
        // Measure flow: anatomical axes from caller-supplied DICOM
        // rowCos/colCos/sliceDir. Lines intersect at bbox center and span
        // the full bbox extent in each direction (endpoints may sit outside
        // the wrap surface by design — each axis represents the volume's
        // max extent in that direction). Rank-colored longest→shortest.
        const rankColors = [0xf87171, 0x4ade80, 0x60a5fa];
        const rankColorHex = ["#f87171", "#4ade80", "#60a5fa"];
        const axes = buildAnatomicalAxes(allPoints, anatomicalAxes);
        const bboxCenter = new THREE.Vector3();
        bbox.getCenter(bboxCenter);
        for (let i = 0; i < axes.length; i++) {
          const { dir, extent } = axes[i];
          const color = rankColors[i];
          const colorHex = rankColorHex[i];
          const half = extent / 2;
          const endPos = bboxCenter.clone().addScaledVector(dir, half);
          const endNeg = bboxCenter.clone().addScaledVector(dir, -half);

          const geom = new THREE.BufferGeometry().setFromPoints([endNeg, endPos]);
          const mat = new THREE.LineBasicMaterial({ color, linewidth: 2 });
          scene.add(new THREE.Line(geom, mat));

          const dotRadius = Math.max(extent, 1) * 0.015;
          for (const pt of [endNeg, endPos]) {
            const dotGeom = new THREE.SphereGeometry(dotRadius, 8, 8);
            const dotMat = new THREE.MeshBasicMaterial({ color });
            const dot = new THREE.Mesh(dotGeom, dotMat);
            dot.position.copy(pt);
            scene.add(dot);
          }
          axisData.push({ length: extent, color: colorHex });
        }
      } else {
        // Labeling flow: per-plane axes along each plane's slice-direction
        // vector, raycast-clamped to the hull from the centroid, colored to
        // match the plane's contour rings.
        const planeDirs: { plane: "axial" | "coronal" | "sagittal"; dir: THREE.Vector3 }[] = [];
        // segResult carries the DICOM sliceDirection per plane. If it's null
        // (polylines came from cornerstone keyframe state, e.g. lasso-only),
        // skip per-plane axes — the hull still renders.
        const sr = segResult;
        if (sr?.sliceDirection) {
          const [x, y, z] = sr.sliceDirection;
          planeDirs.push({ plane: "axial", dir: new THREE.Vector3(x, y, z).normalize() });
        }
        if (sr?.planeContours?.coronal?.sliceDirection) {
          const [x, y, z] = sr.planeContours.coronal.sliceDirection;
          planeDirs.push({ plane: "coronal", dir: new THREE.Vector3(x, y, z).normalize() });
        }
        if (sr?.planeContours?.sagittal?.sliceDirection) {
          const [x, y, z] = sr.planeContours.sagittal.sliceDirection;
          planeDirs.push({ plane: "sagittal", dir: new THREE.Vector3(x, y, z).normalize() });
        }

        for (const { plane, dir } of planeDirs) {
          const color = PLANE_COLORS[plane];
          const colorHex = PLANE_COLORS_HEX[plane];

          let endPos = centroid.clone().add(dir);
          let endNeg = centroid.clone().sub(dir);
          if (hullMesh) {
            raycaster.set(centroid, dir);
            const hitsPos = raycaster.intersectObject(hullMesh);
            if (hitsPos.length > 0) endPos = hitsPos[0].point;
            raycaster.set(centroid, dir.clone().negate());
            const hitsNeg = raycaster.intersectObject(hullMesh);
            if (hitsNeg.length > 0) endNeg = hitsNeg[0].point;
          }

          const length = endPos.distanceTo(endNeg);
          const lineGeom = new THREE.BufferGeometry().setFromPoints([endNeg, endPos]);
          const lineMat = new THREE.LineBasicMaterial({ color, linewidth: 2 });
          scene.add(new THREE.Line(lineGeom, lineMat));

          const dotRadius = Math.max(length, 1) * 0.015;
          for (const pt of [endNeg, endPos]) {
            const dotGeom = new THREE.SphereGeometry(dotRadius, 8, 8);
            const dotMat = new THREE.MeshBasicMaterial({ color });
            const dot = new THREE.Mesh(dotGeom, dotMat);
            dot.position.copy(pt);
            scene.add(dot);
          }

          axisData.push({ length, color: colorHex });
        }
      }
      setHullAxes(axisData);
    } else {
      setHullVolume(null);
      setHullAxes(null);
    }

    // Auto-fit camera
    const center = new THREE.Vector3();
    bbox.getCenter(center);
    const sphere = new THREE.Sphere();
    bbox.getBoundingSphere(sphere);
    const dist = sphere.radius * 2.5;

    camera.position.set(center.x, center.y, center.z + dist);
    controls.target.copy(center);
    controls.update();
  }, [polylines, fillContours, wrapStyle, anatomicalAxes, setHullVolume, setHullAxes]);

  return <div ref={containerRef} className="w-full h-full" />;
};
